/* Who is working, and when — the staff axis of the Today tab.
 *
 * The shifts come from GET /api/trainer-availability/roster, which runs the
 * same weekly-pattern + extra-shifts − approved-leave maths the booking engine
 * uses. The grid only reads them: it hatches the hours outside a shift and
 * badges whoever is on one right now. */

export interface RosterShift {
  /** Minutes from midnight. */
  start: number;
  end: number;
}

export interface RosterStaff {
  id: string;
  name: string;
  /** trainer | therapist | consultant */
  role: string;
  /** false = this provider has no hours set at the branch at all. Not the same
   *  as a day off: we simply don't know, so the column stays unhatched. */
  configured: boolean;
  onLeave: 'full' | 'half' | null;
  shifts: RosterShift[];
}

export interface DayRoster {
  date: string;
  /** The branch's booking day, for reference. */
  day: RosterShift;
  staff: RosterStaff[];
}

const ROLE_LABEL: Record<string, string> = {
  trainer: 'Trainer',
  therapist: 'Therapist',
  consultant: 'Consultant',
};

export const roleLabel = (role?: string | null): string =>
  (role && ROLE_LABEL[role]) || (role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Staff');

/** Is this provider inside one of today's shifts at `min` minutes past midnight? */
export const onShiftAt = (st: Pick<RosterStaff, 'shifts'>, min: number): boolean =>
  st.shifts.some(w => min >= w.start && min < w.end);

/**
 * The gaps between shifts inside the drawn window — what gets hatched.
 *
 * An unconfigured provider returns nothing: hatching their whole column would
 * claim they are off all day when the gym has simply never set their hours.
 */
export function offShiftGaps(st: RosterStaff, startMin: number, endMin: number): RosterShift[] {
  if (!st.configured && !st.onLeave) return [];
  const gaps: RosterShift[] = [];
  let cursor = startMin;
  for (const w of [...st.shifts].sort((a, b) => a.start - b.start)) {
    if (w.start > cursor) gaps.push({ start: cursor, end: Math.min(w.start, endMin) });
    cursor = Math.max(cursor, w.end);
  }
  if (cursor < endMin) gaps.push({ start: cursor, end: endMin });
  return gaps.filter(g => g.end > g.start);
}
